/* global BaseUrl, SeasonPagesEnabled */
import React from 'react';
import { isClip, isMovie, isSports } from 'helpers/ContentHelper';
import {
  helperGetArticleUrl,
  helperGetSeriesArticleUrl,
  helperGetBioUrl,
  helperGetBioLandingUrl,
  helperGetShowUrl,
  helperGetPersonalitySeeAllUrl,
  helperGetPersonalityUrl,
  helperGetBrowseFilterUrl,
  helperGetLiveScheduleUrl,
  helperGetVideoUrl,
} from 'helpers/RouteHelper';

const {
  epg_headerLiveScheduleText: primetimeLabel,
  seriesDetail_castHeader: creditsLabel,
  global_seasonLabel: seasonLabel,
  global_watchButton: watchLabel,
} = window.Language;

const preprendWatchLabel = data => {
  const name = `${watchLabel} ${data.name}`;
  return { ...data, name };
};

export const generateItem = (id, name) => ({ '@id': id, name });

export const getBrowseData = (filterName, selectedTab) => {
  const uri = helperGetBrowseFilterUrl(filterName);
  const capitalized = selectedTab?.charAt(0).toUpperCase() + selectedTab?.slice(1);
  return generateItem(uri, capitalized);
};

export const getShowData = ({ showId, showName, isPersonality, seriesType }) => {
  if (isSports(seriesType)) return null;
  const showUri = isPersonality ? helperGetPersonalityUrl(showId) : helperGetShowUrl(showId);
  return generateItem(showUri, showName);
};

export const getArticleData = (article, params) => {
  const { headline } = article;
  const { pageId, articleId } = params;
  const id = pageId || articleId;
  const uri = helperGetArticleUrl(id);
  return generateItem(uri, headline);
};

export const getSeriesArticleData = article => {
  const { siteScanUrl, headline } = article;
  const uri = helperGetSeriesArticleUrl(siteScanUrl);
  return generateItem(uri, headline);
};

export const getEpisodeName = video => {
  const { name, episodeNumber, seasonNumber, displaySeasonAndEpisodeCounts } = video;
  if (seasonNumber && episodeNumber && displaySeasonAndEpisodeCounts) {
    return `S${seasonNumber}-E${episodeNumber} - ${name}`;
  }
  return name;
};

export const getCreditsData = showId => {
  const bioUri = helperGetBioLandingUrl(showId);
  return generateItem(bioUri, creditsLabel);
};

export const getCreditsDetailData = (showId, pageId, creditsData) => {
  const { name } = creditsData;
  const bioUri = helperGetBioUrl(showId, pageId);
  return generateItem(bioUri, name);
};

export const getMovieData = (showId, showName) => {
  const showUri = helperGetShowUrl(showId);
  return generateItem(showUri, showName);
};

export const getSeeAllData = (personality, series) => {
  const showUri = helperGetPersonalitySeeAllUrl(personality, series);
  return generateItem(showUri, series);
};

export const getEpisodeData = (episodeId, episodeName) => {
  const watchUri = helperGetVideoUrl(episodeId);
  return generateItem(watchUri, episodeName);
};

export const getPrimetimeData = () => {
  const liveScheduleUri = helperGetLiveScheduleUrl();
  return generateItem(liveScheduleUri, primetimeLabel);
};

export const getSeasonData = (showId, seasonNumber) => {
  const addSeasonData = SeasonPagesEnabled && seasonNumber;
  if (!addSeasonData) return null;
  const currentSeasonLabel = `${seasonLabel} ${seasonNumber}`;
  const showUri = helperGetShowUrl(showId);
  const seasonSlug = `season-${seasonNumber}`;
  const seasonUri = `${showUri}${seasonSlug}/`;
  return generateItem(seasonUri, currentSeasonLabel);
};

export const getWatchData = video => {
  const { seriesName: showName, id: episodeId, videoType, seriesType } = video;

  const episodeName = getEpisodeName(video);
  const isMovieData = isMovie(videoType) || isMovie(seriesType);
  const shouldAddWatchLabel = isMovieData && !isClip(videoType);
  const name = isMovieData ? showName : episodeName;
  const data = getEpisodeData(episodeId, name);
  const watchData = shouldAddWatchLabel ? preprendWatchLabel(data) : data;

  return watchData;
};

export const getHelmentItem = (item, index) => {
  const { '@id': id, name } = item;
  const position = index + 1;

  return {
    '@type': 'ListItem',
    position,
    item: generateItem(`${BaseUrl}${id}`, name),
  };
};

export const getHelmetData = screenData => {
  const itemListElement = screenData.map(getHelmentItem);
  const json = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };

  return <script type="application/ld+json">{JSON.stringify(json)}</script>;
};
